import { useEffect, useState } from 'react';
import { Clock, Calendar, Save, X, Users, Loader2, RefreshCw } from 'lucide-react';

const WEEKDAYS = [
  { id: 1, label: 'Isn' },
  { id: 2, label: 'Sel' },
  { id: 3, label: 'Rab' },
  { id: 4, label: 'Kha' },
  { id: 5, label: 'Jum' },
  { id: 6, label: 'Sab' },
  { id: 7, label: 'Aha' },
];

const PRESET_LABELS = {
  office: '🏢 Office (9-6, grace 30m)',
  trader: '📈 Trader (7-4, grace 15m)',
  sales: '💼 Sales (10-7, grace 30m)',
  creative: '🎨 Creative (10-7, grace 60m)',
  ops_ib: '⚙️ Ops IB (8-5, grace 15m)',
  weekend: '📅 Weekend (10-6 Sab/Ahd)',
  custom: '🛠️ Custom',
};

export default function WorkScheduleManager({ isOpen, onClose, staffList = [], onSaved }) {
  const apiUrl = process.env.REACT_APP_BACKEND_URL;
  const [presets, setPresets] = useState({});
  const [mode, setMode] = useState('individual'); // 'individual' | 'department'
  const [loading, setLoading] = useState(false);

  // Form state
  const [selectedStaffId, setSelectedStaffId] = useState('');
  const [selectedDept, setSelectedDept] = useState('');
  const [overwriteExisting, setOverwriteExisting] = useState(true);
  const [schedule, setSchedule] = useState({
    work_start: '09:00',
    work_end: '18:00',
    grace_minutes: 30,
    work_days: [1, 2, 3, 4, 5],
    shift_type: 'office',
  });

  // Schedule by-department overview
  const [overview, setOverview] = useState([]);

  // Departments derived from staff list
  const departments = Array.from(new Set(staffList.map((s) => s.department).filter(Boolean))).sort();

  useEffect(() => {
    if (!isOpen) return;
    const load = async () => {
      try {
        const [pr, ov] = await Promise.all([
          fetch(`${apiUrl}/api/staff/schedule/presets`, { credentials: 'include' }).then((r) => r.json()),
          fetch(`${apiUrl}/api/staff/schedule/by-department`, { credentials: 'include' }).then((r) => r.json()),
        ]);
        setPresets(pr.presets || {});
        setOverview(ov.groups || []);
      } catch (e) {
        console.error('preset load failed', e);
      }
    };
    load();
  }, [isOpen, apiUrl]);

  const applyPreset = (key) => {
    if (key === 'custom') {
      setSchedule((s) => ({ ...s, shift_type: 'custom' }));
      return;
    }
    const p = presets[key];
    if (p) setSchedule({ ...p });
  };

  const toggleDay = (id) => {
    setSchedule((s) => ({
      ...s,
      work_days: s.work_days.includes(id) ? s.work_days.filter((d) => d !== id) : [...s.work_days, id].sort(),
    }));
  };

  const reloadOverview = async () => {
    try {
      const ov = await fetch(`${apiUrl}/api/staff/schedule/by-department`, { credentials: 'include' }).then((r) => r.json());
      setOverview(ov.groups || []);
    } catch (e) { /* ignore */ }
  };

  const handleSave = async () => {
    if (schedule.work_days.length === 0) {
      alert('Sila pilih sekurang-kurangnya 1 hari bekerja');
      return;
    }
    setLoading(true);
    try {
      let res;
      if (mode === 'individual') {
        if (!selectedStaffId) {
          alert('Sila pilih staff');
          setLoading(false);
          return;
        }
        res = await fetch(`${apiUrl}/api/staff/${selectedStaffId}/schedule`, {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(schedule),
        });
      } else {
        if (!selectedDept) {
          alert('Sila pilih department');
          setLoading(false);
          return;
        }
        res = await fetch(`${apiUrl}/api/staff/schedule/bulk-by-department`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ department: selectedDept, schedule, overwrite_existing: overwriteExisting }),
        });
      }
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Update gagal');

      const msg = mode === 'individual'
        ? `✅ Jadual untuk staff dikemas kini`
        : `✅ ${data.modified} staff (${data.matched} matched) di department "${selectedDept}" dikemas kini`;
      alert(msg);
      await reloadOverview();
      onSaved?.();
    } catch (err) {
      alert('Ralat: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    if (mode !== 'individual' || !selectedStaffId) return;
    if (!window.confirm('Reset jadual ke default (09:00-18:00)?')) return;
    setLoading(true);
    try {
      const res = await fetch(`${apiUrl}/api/staff/${selectedStaffId}/schedule`, {
        method: 'DELETE', credentials: 'include',
      });
      if (!res.ok) throw new Error('Reset gagal');
      alert('✅ Jadual direset ke default');
      await reloadOverview();
      onSaved?.();
    } catch (err) {
      alert('Ralat: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="work-schedule-modal">
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[92vh] overflow-y-auto">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-red-700" />
            <h3 className="font-semibold text-gray-800">Tetapkan Waktu Bekerja Staff</h3>
          </div>
          <button onClick={onClose} data-testid="ws-close-btn" className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Mode toggle */}
          <div className="flex gap-2 border border-gray-200 rounded-lg p-1 bg-gray-50">
            <button
              onClick={() => setMode('individual')}
              data-testid="ws-mode-individual"
              className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition ${mode === 'individual' ? 'bg-white text-red-700 shadow-sm' : 'text-gray-600'}`}
            >
              Per Staff
            </button>
            <button
              onClick={() => setMode('department')}
              data-testid="ws-mode-department"
              className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition ${mode === 'department' ? 'bg-white text-red-700 shadow-sm' : 'text-gray-600'}`}
            >
              <Users className="inline w-4 h-4 mr-1" /> Bulk per Department
            </button>
          </div>

          {/* Selector */}
          {mode === 'individual' ? (
            <div>
              <label className="text-sm text-gray-700 font-medium mb-1 block">Pilih Staff</label>
              <select
                value={selectedStaffId}
                onChange={(e) => setSelectedStaffId(e.target.value)}
                data-testid="ws-staff-select"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
              >
                <option value="">-- Pilih Staff --</option>
                {staffList
                  .filter((s) => s.status === 'Active')
                  .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                  .map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} {s.department ? `(${s.department})` : ''}
                    </option>
                  ))}
              </select>
            </div>
          ) : (
            <div>
              <label className="text-sm text-gray-700 font-medium mb-1 block">Pilih Department</label>
              <select
                value={selectedDept}
                onChange={(e) => setSelectedDept(e.target.value)}
                data-testid="ws-dept-select"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
              >
                <option value="">-- Pilih Department --</option>
                {departments.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
              <label className="flex items-center gap-2 text-xs text-gray-600 mt-2">
                <input
                  type="checkbox"
                  checked={overwriteExisting}
                  onChange={(e) => setOverwriteExisting(e.target.checked)}
                  data-testid="ws-overwrite-checkbox"
                />
                Overwrite jadual sedia ada (uncheck untuk staff baru sahaja)
              </label>
            </div>
          )}

          {/* Quick presets */}
          <div>
            <label className="text-sm text-gray-700 font-medium mb-2 block">Quick Preset</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {Object.keys(PRESET_LABELS).map((k) => (
                <button
                  key={k}
                  onClick={() => applyPreset(k)}
                  data-testid={`ws-preset-${k}`}
                  className={`px-3 py-2 text-xs border rounded-lg text-left hover:border-red-500 hover:bg-red-50 transition ${schedule.shift_type === k ? 'border-red-700 bg-red-50' : 'border-gray-200'}`}
                >
                  {PRESET_LABELS[k]}
                </button>
              ))}
            </div>
          </div>

          {/* Schedule fields */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-gray-600 mb-1 block">Mula Kerja</label>
              <input
                type="time"
                value={schedule.work_start}
                onChange={(e) => setSchedule({ ...schedule, work_start: e.target.value, shift_type: 'custom' })}
                data-testid="ws-work-start"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="text-xs text-gray-600 mb-1 block">Habis Kerja</label>
              <input
                type="time"
                value={schedule.work_end}
                onChange={(e) => setSchedule({ ...schedule, work_end: e.target.value, shift_type: 'custom' })}
                data-testid="ws-work-end"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="text-xs text-gray-600 mb-1 block">Grace (minit)</label>
              <input
                type="number"
                min="0" max="240"
                value={schedule.grace_minutes}
                onChange={(e) => setSchedule({ ...schedule, grace_minutes: parseInt(e.target.value, 10) || 0 })}
                data-testid="ws-grace"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
              />
              <p className="text-[10px] text-gray-500 mt-1">Lewat selepas {schedule.work_start} + {schedule.grace_minutes}m</p>
            </div>
          </div>

          {/* Work days */}
          <div>
            <label className="text-sm text-gray-700 font-medium mb-2 block flex items-center gap-1">
              <Calendar className="w-4 h-4" /> Hari Bekerja
            </label>
            <div className="flex gap-2 flex-wrap">
              {WEEKDAYS.map((d) => (
                <button
                  key={d.id}
                  onClick={() => toggleDay(d.id)}
                  data-testid={`ws-day-${d.id}`}
                  className={`px-3 py-2 text-xs rounded-lg border min-w-[44px] transition ${schedule.work_days.includes(d.id) ? 'bg-red-700 text-white border-red-700' : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'}`}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>

          {/* Overview Table */}
          {overview.length > 0 && (
            <div className="border-t pt-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">📋 Senarai Jadual Semasa (per Department)</h4>
              <div className="overflow-x-auto max-h-48 overflow-y-auto border border-gray-100 rounded-lg">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="text-left p-2">Department</th>
                      <th className="text-left p-2">Shift</th>
                      <th className="text-center p-2">Mula</th>
                      <th className="text-center p-2">Habis</th>
                      <th className="text-right p-2">Staff</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {overview.map((g, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="p-2 text-gray-700">{g.department}</td>
                        <td className="p-2 text-gray-600">{g.shift_type}</td>
                        <td className="p-2 text-center">{g.work_start}</td>
                        <td className="p-2 text-center">{g.work_end}</td>
                        <td className="p-2 text-right font-medium">{g.staff_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-gray-100 flex items-center justify-between sticky bottom-0 bg-white">
          {mode === 'individual' && selectedStaffId ? (
            <button
              onClick={handleReset}
              data-testid="ws-reset-btn"
              disabled={loading}
              className="px-3 py-2 text-sm text-gray-600 hover:text-red-600 flex items-center gap-1"
            >
              <RefreshCw className="w-4 h-4" /> Reset ke Default
            </button>
          ) : <div />}
          <div className="flex gap-2">
            <button
              onClick={onClose}
              data-testid="ws-cancel-btn"
              className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              Batal
            </button>
            <button
              onClick={handleSave}
              data-testid="ws-save-btn"
              disabled={loading}
              className="px-5 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800 flex items-center gap-2 text-sm disabled:opacity-60"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {mode === 'individual' ? 'Simpan' : 'Apply Bulk'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
